// Contact-gate submission log: every initial-form submission (name/email/org/
// role + timestamp), flagged blocked or allowed. Read-only; lets sales follow
// up with people who never finished an inquiry and spot-check blocked addresses.
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress, Chip, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { fetchContactSubmissions } from '../../../store/admin-inquiries-slice.js';
import { formatDateTime } from '../../../utils/admin-inquiry-format.js';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'allowed', label: 'Allowed' },
  { value: 'blocked', label: 'Blocked' },
];

const filterToArg = (filter) => {
  if (filter === 'blocked') return { blocked: true };
  if (filter === 'allowed') return { blocked: false };
  return {};
};

const ContactSubmissionsTable = () => {
  const dispatch = useDispatch();
  const {
    submissions, submissionsLoading, submissionsError,
  } = useSelector((s) => s.adminInquiries);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    dispatch(fetchContactSubmissions(filterToArg(filter)));
  }, [dispatch, filter]);

  const renderBody = () => {
    if (submissionsLoading && submissions.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading form submissions" />
        </Box>
      );
    }
    if (submissions.length === 0) {
      return <Alert severity="info">No form submissions match this filter.</Alert>;
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Submitted</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {submissions.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{formatDateTime(row.created_at)}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.contact_name}</Typography>
                <Typography variant="caption" color="text.secondary">{row.contact_email}</Typography>
              </TableCell>
              <TableCell>{row.contact_org}</TableCell>
              <TableCell>{row.contact_role || '—'}</TableCell>
              <TableCell>
                {row.blocked ? (
                  <Chip
                    label={row.block_rule ? `Blocked · ${row.block_rule}` : 'Blocked'}
                    size="small"
                    color="error"
                  />
                ) : (
                  <Chip label="Allowed" size="small" color="success" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      {submissionsError && <Alert severity="error" sx={{ mb: 2 }}>{submissionsError}</Alert>}

      <Stack direction="row" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, next) => { if (next !== null) setFilter(next); }}
          aria-label="Filter form submissions"
        >
          {FILTERS.map((f) => (
            <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {renderBody()}
    </Box>
  );
};

export default ContactSubmissionsTable;
